import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchDirectAwardDetails,
  fetchDirectAwardAdditionalData,
} from '../../services/publicTendersApi';
import './DirectAwardDetails.css';
import Utility from './../../utils/Utility';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import Breadcrumbs from '../../components/Breadcrumbs';
import RestrictedAccess from '../../components/RestrictedAccess/RestrictedAccess';
import ConversionPopup from '../../components/ConversionPopup/ConversionPopup';
import ProcedurePaywall from '../../components/ProcedurePaywall/ProcedurePaywall';
import { slugify } from '../../utils/slugify';
import { useAuth } from '../../context/AuthContext';
import pdfIcon from '../../assets/icons/pdf-icon.png';
import docIcon from '../../assets/icons/doc-icon.png';
import xlsIcon from '../../assets/icons/xls-icon.png';
import zipIcon from '../../assets/icons/zip-icon.png';
import defaultIcon from '../../assets/icons/file-icon.png';
import ItemLoadingHolder from '../../components/LoadingHolders/ItemLoadingHolder';
import GenerateOfferButton from '../../components/GenerateOfferButton/GenerateOfferButton';
import CDERequestButton from '../../components/CDERequestButton/CDERequestButton';
import PremiumAIReport from '../../components/PremiumAIReport/PremiumAIReport';
import ProjectBadge from '../../components/ProjectBadge/ProjectBadge';
import SaveProcedureButton from '../../components/SaveProcedureButton/SaveProcedureButton';
import FileDownloadButton from '../../components/FileDownloadButton/FileDownloadButton';
import TruncatedText from '../../components/TruncatedText/TruncatedText';

const DirectAwardDetails = () => {
  const { noticeNo: id } = useParams(); // URL param now holds mongoId
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConversionPopup, setShowConversionPopup] = useState(false);

  // Verificăm dacă utilizatorul este autentificat
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    let mounted = true;
    const getDetails = async () => {
      try {
        setLoading(true);
        const data = await fetchDirectAwardDetails(id);
        if (!mounted) return;
        setDetails(data);
      } catch (err) {
        if (!mounted) return;
        setError(err.message || 'Something went wrong');
      } finally {
        if (mounted) setLoading(false);
        if (window.prerenderReady !== undefined) {
          window.prerenderReady = true;
        }
      }
    };

    getDetails();
    return () => {
      mounted = false;
    };
  }, [id]);

  // Refresh ADV data from e-licitatie.ro after the initial paint so download buttons
  // use current DocumentUrl / DocumentName. Stale values cause 404s on download.
  // Skip silently if anonymous (endpoint is subscription-gated and returns 401/403) —
  // anonymous users can't download anyway.
  useEffect(() => {
    if (!details || !isAuthenticated) return;
    let mounted = true;
    (async () => {
      try {
        const fresh = await fetchDirectAwardAdditionalData(id);
        if (!mounted || !fresh?.advForView) return;
        setDetails((prev) =>
          prev ? { ...prev, advForView: fresh.advForView } : prev,
        );
      } catch (err) {
        // Non-fatal: keep MongoDB-cached values. Download may still fail, but the
        // rest of the page (title, value, authority) stays usable.
        console.warn('ADV refresh failed:', err?.response?.status || err?.message);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, isAuthenticated, details?.advForView?.advertisingNoticeId]);

  // Afișează popup-ul de conversie pentru utilizatorii neautentificați
  useEffect(() => {
    if (!loading && !error && details && !isAuthenticated) {
      const timer = setTimeout(() => {
        setShowConversionPopup(true);
      }, 5000); // Afișează popup-ul după 5 secunde

      return () => clearTimeout(timer);
    }
  }, [loading, error, details, isAuthenticated]);

  const getFileIcon = (extension) => {
    switch (extension) {
      case 'pdf':
        return pdfIcon;
      case 'doc':
      case 'docx':
        return docIcon;
      case 'xls':
      case 'xlsx':
        return xlsIcon;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
        return zipIcon;
      default:
        return defaultIcon;
    }
  };

  const handleFileDownload = async () => {
    var noticeDocumentName = details.advForView.documentName;
    var noticeDocumentUrl = details.advForView.documentUrl;

    const advNoticeId = details.advForView?.advertisingNoticeId;
    const noticeNo = details.advForView?.noticeNo;

    if (!noticeDocumentUrl || !noticeDocumentName || !advNoticeId) {
      console.error('Missing data for file download.');
      alert('Some required information is missing. Please try again.');
      return;
    }

    try {
      const url = `${
        import.meta.env.VITE_BASE_URL
      }/api/DirectAwardListWeb/download/?noticeDocumentUrl=${
        noticeDocumentUrl
      }&noticeDocumentName=${encodeURIComponent(
        noticeDocumentName,
      )}&advNoticeId=${advNoticeId}
      &noticeNo=${encodeURIComponent(noticeNo)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download the file. Please try again.');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');

      let fileName = noticeDocumentName.replace(/\.p7m|\.p7s$/, '');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error during file download:', error);
      alert('An error occurred while attempting to download the file.');
    }
  };

  if (loading) {
    return <ItemLoadingHolder />;
  }

  if (error) {
    return <p className="error-text">Eroare: {error}</p>;
  }

  if (!details) {
    return <p>Nu există date disponibile.</p>;
  }

  var labelItem =
    // `<b>${details.advForView?.noticeNo || 'N/A'}</b> ` +
    details.advForView?.contractObject || 'N/A';

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { href: '/achizitii-directe', label: 'Achiziții Directe' },
    { label: labelItem },
  ];

  return (
    <>
      <SeoPageComponent
        pageTitle={`${details.advForView?.contractObject || 'Detalii Achiziție Directă'} - ${import.meta.env.VITE_NAME}`}
        pageDescription={`Informații despre achiziția directă ${details.stickyData?.contractTitle || 'N/A'}, publicată de ${details.stickyData?.caName || 'N/A'}.`}
        pageUrl={`${import.meta.env.VITE_DOMAIN}/achizitii-directe/${slugify(details.advForView?.contractObject)}/${id}`}
      />
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />

      {/* Titlu complet, vizibil */}
      <section className="pt-10 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <h1 className="text-2xl md:text-4xl font-extrabold text-indigo-700 text-center mb-4 break-words drop-shadow-xl">
            {details.advForView?.contractObject || 'Detalii Achiziție Directă'}
          </h1>
        </div>
      </section>

      {/* Summary bar compact, smart, vizibil rapid */}
      <section className="pb-6 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-2 md:gap-4 bg-white/80 rounded-xl shadow-lg px-4 py-4 md:py-6 mb-6 border border-indigo-100">
            <div className="flex flex-wrap gap-2 justify-center items-center w-full md:w-auto">
              {!details.isPartial && (
                <span className="bg-green-100 text-green-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-green-200 shadow-sm">
                  {details.advForView?.noticeNo || 'N/A'}
                </span>
              )}
              {!details.isPartial && (
                <span className="bg-yellow-100 text-yellow-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-yellow-200 shadow-sm">
                  {details.advForView?.contractAuthorityName || 'N/A'}
                </span>
              )}
              <span className="bg-purple-100 text-purple-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-purple-200 shadow-sm">
                {details.advForView?.awardCriteria || 'N/A'}
              </span>
              <span className="bg-indigo-100 text-indigo-700 text-xs md:text-sm font-bold px-3 py-1 rounded-full border border-indigo-200 shadow-sm">
                {Utility.ConvertEstimatedValue(
                  details.advForView?.estimatedValue,
                )}{' '}
                {details.advForView?.currency?.text || 'RON'}
              </span>
              {details.advForView?.cpvCode?.text && (
                <span className="bg-blue-100 text-blue-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-blue-200 shadow-sm">
                  CPV: {details.advForView.cpvCode.text}
                </span>
              )}
            </div>
            <div className="w-full md:w-auto flex justify-center md:justify-end mt-2 md:mt-0">
              <SaveProcedureButton noticeNo={details.advForView?.noticeNo} />
            </div>
          </div>
        </div>
      </section>

      {/* Detalii compacte, organizate pe grid modern */}
      <section className="pb-16 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          {/* Primul rând: Detalii Achiziție, Termene, Coduri CPV & Fișiere */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Detalii Achiziție */}
            <div className="bg-white shadow-lg rounded-xl p-6 flex flex-col gap-4 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                Detalii Achiziție
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Titlu:</span>
                  <span
                    className="truncate"
                    title={details.advForView?.contractObject}
                  >
                    {details.advForView?.contractObject || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Număr:</span>
                  <RestrictedAccess
                    compact={true}
                    message="Numar licitatie disponibil doar pentru utilizatorii autentificati"
                  >
                    <span className="text-green-700 font-semibold bg-green-50 px-2 py-0.5 rounded text-sm">
                      {details.advForView?.noticeNo || 'N/A'}
                    </span>
                  </RestrictedAccess>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Autoritate:</span>
                  <span className="text-yellow-700 font-semibold bg-yellow-50 px-2 py-0.5 rounded text-sm">
                    {details.advForView?.contractAuthorityName || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Criterii atribuire:</span>
                  <span className="text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded text-sm">
                    {details.advForView?.awardCriteria || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Valoare Estimată:</span>
                  <span className="text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded text-sm">
                    {Utility.ConvertEstimatedValue(
                      details.advForView?.estimatedValue,
                    )}{' '}
                    {details.advForView?.currency?.text || 'RON'}
                  </span>
                </div>
              </div>
            </div>

            {/* Termene */}
            <div className="bg-white shadow-lg rounded-xl p-6 flex flex-col gap-4 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Termene
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div>
                  <span className="font-medium">Data publicării:</span>{' '}
                  {Utility.FormatDate(details.advForView?.publicationDate) ||
                    'N/A'}
                </div>
                <div>
                  <span className="font-medium">Termen Limită:</span>{' '}
                  {Utility.FormatDate(
                    details.advForView?.tenderReceiptDeadline,
                  ) || 'N/A'}
                </div>
              </div>
            </div>

            {/* Coduri CPV & Fișiere */}
            <div className="bg-white shadow-lg rounded-xl p-6 flex flex-col gap-4 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                Coduri CPV & Fișiere
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div>
                  <span className="font-medium">Cod CPV:</span>{' '}
                  {details.advForView?.cpvCode?.text || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Fișiere disponibile:</span>
                  <RestrictedAccess message="Sectiunea fisiere disponibila doar pentru utilizatorii autentificati">
                    <div className="space-y-2 mt-1">
                      {details.advForView?.documentUrl &&
                      details.advForView?.documentName ? (
                        <FileDownloadButton
                          fileName={details.advForView.documentName}
                          onDownload={handleFileDownload}
                        />
                      ) : (
                        <p className="text-sm text-gray-500">
                          Nu există fișiere disponibile.
                        </p>
                      )}
                    </div>
                  </RestrictedAccess>
                </div>
              </div>
            </div>
          </div>

          {/* Servicii premium */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <GenerateOfferButton auctionId={details.advForView?.noticeNo || id} variant="section" />
            <CDERequestButton auctionId={details.advForView?.noticeNo || id} variant="section" />
            <PremiumAIReport noticeNo={details.advForView?.noticeNo || id} type="directAward" />
            <ProjectBadge auctionId={details.advForView?.noticeNo || id} variant="section" />
          </div>

          {/* Al treilea rând: Descriere Contract, Condiții de Participare, Informații Adiționale, Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Descriere contract */}
            {details.advForView?.contractDescription && (
              <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
                <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Descriere Contract
                </h2>
                <TruncatedText text={details.advForView.contractDescription} />
              </div>
            )}
            {/* Conditii de participare */}
            <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Conditii de Participare
              </h2>
              <TruncatedText text={details.advForView.participationConditions} />
            </div>
            {/* Informatii aditionale */}
            <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                Informatii Aditionale
              </h2>
              <TruncatedText text={details.advForView.additionalInformation} />
            </div>
            {/* Contact Autoritate */}
            <div className="bg-white shadow-lg rounded-xl p-6 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-indigo-600 text-lg font-bold mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                Contact
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div>
                  <span className="font-medium">Autoritate:</span>
                  <p className="mt-1">
                    {details.advForView?.contractAuthorityName || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="font-medium">Tip procedură:</span>
                  <p className="mt-1">
                    {details.advForView?.awardCriteria || 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="font-medium">Status:</span>
                  <span className="inline-block mt-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                    Activă
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Paywall pentru utilizatorii fără abonament */}
      {details.isPartial && <ProcedurePaywall />}

      {/* Popup de conversie pentru utilizatorii neautentificați */}
      <ConversionPopup
        isOpen={showConversionPopup}
        onClose={() => setShowConversionPopup(false)}
        onSuccess={() => {
          // După înregistrarea cu succes, reîmprospătăm pagina
          window.location.reload();
        }}
      />
    </>
  );
};

export default DirectAwardDetails;
